import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Loadable from 'react-loadable';
import {CSSTransition, TransitionGroup} from 'react-transition-group';

import FeatureToggle from '@stubhub/react-feature-toggle';
import InterceptModal from '@stubhub/react-intercept-modal';
import LayoutHandler from '@stubhub/react-mobile-layout-handler';
import Spinner from '@stubhub/react-spinner';

import {importantMessagesCookieExists, setImportantMessagesCookie} from '../../../../utils/importantMessagesUtils';
import ImportantInfoModal from '../../components/react-important-info-modal';
import BuildYourOrder from '../../containers/react-build-your-order';
import ListingsFilter from '../../containers/react-listings-filter';
import {NoListingFilter} from '../../containers/react-no-listings';
import RoyalMap from '../../containers/react-royal-map';
import TicketList from '../../containers/react-ticket-list';

import './index.scss';

const EventInfoHeader = Loadable({
  loader: () => import(/* webpackChunkName: "swi-eventInfoHeader" */ '../../containers/react-event-info-header'),
  loading: Spinner,
});

// This mapping is necessary to connect the component import to string based declaration from props
const sectionComponents = {
  TicketList,
  BuildYourOrder,
  RoyalMap,
  ListingsFilter,
};

export const handleIsImportantInfoModalOpen = (
  cookies,
  {showImportantInfoModal, showDisclaimer, forceShowDisclaimer, eventId}
) => {
  return (
    showImportantInfoModal &&
    ((showDisclaimer && !importantMessagesCookieExists(cookies, eventId)) || forceShowDisclaimer)
  );
};

@LayoutHandler()
class EventRoyal extends Component {
  constructor(props, context) {
    super(props, context);
    const {showImportantInfoModal, showDisclaimer, forceShowDisclaimer, eventId} = this.props;
    const {cookies} = this.context;
    this.state = {
      listingAnimationEnabled: false,
      isImportantInfoModalOpen: handleIsImportantInfoModalOpen(cookies, {
        showImportantInfoModal,
        showDisclaimer,
        forceShowDisclaimer,
        eventId,
      }),
    };
  }
  static contextTypes = {
    cookies: PropTypes.object,
    globalRegistry: PropTypes.object.isRequired,
    track: PropTypes.object.isRequired,
  };

  onCloseBYO = (e) => {
    const {onCloseBYO} = this.props;
    this.setState({
      listingAnimationEnabled: true,
    });
    onCloseBYO(e);
  };

  handleSelectTicket = (...args) => {
    const {handleSelectTicket} = this.props;
    this.setState({
      listingAnimationEnabled: true,
    });
    handleSelectTicket && handleSelectTicket(...args);
  };

  onResetFilter = (...args) => {
    const {onResetFilter} = this.props;
    onResetFilter(...args);
    this.setState({
      listingAnimationEnabled: false,
    });
  };

  renderBYO() {
    const {
      hasTickets,
      showBYO,
      listingId,
      deviceType,
      selectedTicketRank,
      partner,
      layoutConfig,
      btToken,
      paypalClientId,
      eventCurrency,
      paypalError,
    } = this.props;
    const LeftAnimatedPanel = sectionComponents[layoutConfig.BYOTakeover];

    if (hasTickets && showBYO && LeftAnimatedPanel) {
      /* Left Animated Panel component abstraction */
      return (
        <LeftAnimatedPanel
          listingId={listingId}
          onCloseBYO={this.onCloseBYO}
          selectedTicketRank={selectedTicketRank}
          deviceType={deviceType}
          partner={partner}
          btToken={btToken}
          paypalClientId={paypalClientId}
          eventCurrency={eventCurrency}
          paypalError={paypalError}
        />
      );
    }
  }

  componentDidMount() {
    document && document.body.classList.add('EventRoyalBody--desktop');
  }

  componentDidUpdate(prevProps) {
    const {
      gtmEventFinishedLoadingTrack,
      loading,
      showDisclaimer,
      buyerMessages,
      forceShowDisclaimer,
      showImportantInfoModal,
      eventId,
    } = this.props;

    if (prevProps.loading && !loading) {
      gtmEventFinishedLoadingTrack();
    }

    if (
      prevProps.showDisclaimer !== showDisclaimer ||
      prevProps.buyerMessages !== buyerMessages ||
      prevProps.forceShowDisclaimer !== forceShowDisclaimer
    ) {
      const {cookies} = this.context;
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isImportantInfoModalOpen: handleIsImportantInfoModalOpen(cookies, {
          showImportantInfoModal,
          showDisclaimer,
          forceShowDisclaimer,
          eventId,
        }),
      });
    }
  }

  componentWillUnmount() {
    document && document.body.classList.remove('EventRoyalBody--desktop');
  }

  renderTicketList() {
    const {
      hasTickets,
      showBYO,
      deviceType,
      totalListings,
      listingLoading = true,
      partner,
      buyerMessages,
      layoutConfig,
    } = this.props;
    const {listingAnimationEnabled} = this.state;
    const enterAnimation = listingAnimationEnabled ? 'slideLeft-enter' : '';
    const exitAnimation = listingAnimationEnabled ? 'slideLeft-exit' : 'slideLeft-exit slideLeft-exit--done';
    const LeftPanel = sectionComponents[layoutConfig.LeftPanel];

    let ticketListContent = <Spinner />;
    if (hasTickets && totalListings > 0 && LeftPanel) {
      /* Left Panel component abstraction */
      ticketListContent = (
        <LeftPanel
          handleSelectTicket={this.handleSelectTicket}
          deviceType={deviceType}
          partner={partner}
          buyerMessages={buyerMessages}
        />
      );
    } else if (!listingLoading && totalListings === 0) {
      ticketListContent = <NoListingFilter onResetFilter={this.onResetFilter} />;
    }

    return (
      <div
        className={`EventRoyal__DesktopLayout__BodyContainer__LeftRail__AnimationContainer__TicketList ${
          showBYO ? exitAnimation : enterAnimation
        }`}
      >
        {ticketListContent}
      </div>
    );
  }

  handleCloseImportantInfoModal = () => {
    const {eventId, closeDisclaimer} = this.props;
    const {cookies} = this.context;
    const importantMessagesCookie = importantMessagesCookieExists(cookies, eventId);
    if (!importantMessagesCookie) {
      setImportantMessagesCookie(cookies, eventId);
    }
    closeDisclaimer();
  };

  render() {
    const {
      eventId,
      averageTicketPriceWithCurrency,
      showBYO,
      enableMerchHeader,
      layoutConfig,
      currentLocation = {},
      defaultWebTLD = '',
      buyerMessages,
      history,
      mapInventoryLoading,
      goBack,
      hasTickets,
      loading,
      listingLoading,
    } = this.props;
    const {isImportantInfoModalOpen} = this.state;
    const CentralPanel = sectionComponents[layoutConfig.MainPanel];
    const RightPanel = sectionComponents[layoutConfig.RightPanel];
    const isVisible = hasTickets || loading || listingLoading;

    return (
      <div className="EventRoyal__container">
        <div
          className={cx('EventRoyal__DesktopLayout', {'EventRoyal__DesktopLayout--withMerchHeader': enableMerchHeader})}
        >
          {isImportantInfoModalOpen && (
            <ImportantInfoModal
              header={buyerMessages[0].header}
              message={buyerMessages[0].message}
              onClose={this.handleCloseImportantInfoModal}
            />
          )}

          <section className="EventRoyale__DesktopLayout__SubHeader">
            {!enableMerchHeader && <EventInfoHeader data-testid="event-info-header" />}
          </section>
          <div className="EventRoyal__DesktopLayout__BodyContainer">
            <section className={`EventRoyal__DesktopLayout__BodyContainer__LeftRail ${isVisible ? 'visible' : ''}`}>
              {this.renderTicketList()}
              <TransitionGroup component={null}>
                <CSSTransition timeout={500} classNames="slideRight" key={showBYO} appear>
                  <div className="EventRoyal__DesktopLayout__BodyContainer__LeftRail__AnimationContainer__BYO">
                    {this.renderBYO()}
                  </div>
                </CSSTransition>
              </TransitionGroup>
            </section>
            {CentralPanel && (
              <section
                className={`EventRoyal__DesktopLayout__BodyContainer__MiddleContent ${isVisible ? '' : 'visible'}`}
              >
                {mapInventoryLoading && <Spinner />}
                <CentralPanel handleCloseBYO={this.onCloseBYO} history={history} />
              </section>
            )}
            {RightPanel && (
              <section className={`EventRoyal__DesktopLayout__BodyContainer__RightRail ${isVisible ? 'visible' : ''}`}>
                <RightPanel
                  showFilterHeader={false}
                  showFilterFooter={false}
                  eventId={eventId}
                  averageTicketPriceWithCurrency={averageTicketPriceWithCurrency}
                  onResetFilter={(e) => this.onResetFilter(e)}
                  showSlideUpResetButton
                  goBack={goBack}
                />
              </section>
            )}
          </div>
        </div>
        <FeatureToggle name="common.enableInterceptModalForCA">
          <InterceptModal currentLocation={currentLocation} defaultWebTLD={defaultWebTLD} />
        </FeatureToggle>
      </div>
    );
  }
}

EventRoyal.propTypes = {
  /** Average ticket price with currency object */
  averageTicketPriceWithCurrency: PropTypes.object,

  /** Braintree token */
  btToken: PropTypes.string,

  /** Important messages for the event */
  buyerMessages: PropTypes.array,

  /** Function called when important message is closed */
  closeDisclaimer: PropTypes.func.isRequired,

  /** Current location object */
  currentLocation: PropTypes.object,

  /** Default TLD for the site */
  defaultWebTLD: PropTypes.string,

  /** The device type. should be `desktop` or `mobile` */
  deviceType: PropTypes.string,

  /** Is merch header enabled? */
  enableMerchHeader: PropTypes.bool,

  /** The event currency */
  eventCurrency: PropTypes.string,

  /** The event id */
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** If the important message should be shown regardless of the cookie value */
  forceShowDisclaimer: PropTypes.bool,

  /** The router history object */
  history: PropTypes.object,

  /** Function to call when back is clicked */
  goBack: PropTypes.func,

  /** Gtm track function */
  gtmEventFinishedLoadingTrack: PropTypes.func.isRequired,

  /** The layout config for event page */
  layoutConfig: PropTypes.object,

  /** If event is loading */
  loading: PropTypes.bool,

  /** Is map inventory loading? */
  mapInventoryLoading: PropTypes.bool,

  /** Handler function invoked when a function is selected */
  handleSelectTicket: PropTypes.func,

  /** If the event has tickets */
  hasTickets: PropTypes.bool,

  /** Selected listing id */
  listingId: PropTypes.number,

  /** If the listing is loading */
  listingLoading: PropTypes.bool,

  /** Partner id */
  partner: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** Paypal client id */
  paypalClientId: PropTypes.string,

  /** If there's any paypal loading error */
  paypalError: PropTypes.bool,

  /** Ticket rank of the selected listing */
  selectedTicketRank: PropTypes.number,

  /** If BYO should be shown */
  showBYO: PropTypes.bool,

  /** If there is any important message to be shown */
  showDisclaimer: PropTypes.bool,

  /** If the important message should be shown in a modal */
  showImportantInfoModal: PropTypes.bool,

  /** Total number of listings */
  totalListings: PropTypes.number,

  /** Function to call when BYO is closed */
  onCloseBYO: PropTypes.func.isRequired,

  /** Function to call when filter is reset */
  onResetFilter: PropTypes.func.isRequired,
};

export default EventRoyal;
